<template>
  <div>
    <provisioning-status @refresh="load" />
    <v-card class="main-card">
      <v-card-title class="screen-title">
        <span>Microsoft Peering向けNAT一覧</span>
        <manual-button path="/ms-peering-nat-list/" />
      </v-card-title>
      <v-card-text class="pa-2" v-if="isLoaded">
        <v-row no-gutters>
          <v-col>
            <app-search
              :search="natSetting.search"
              v-model="natSetting.filter"
            />
          </v-col>
          <div class="d-flex flex-row ga-1 flex-wrap ml-2">
            <v-btn color="primary" @click="downloadCsv">一覧ダウンロード</v-btn>
          </div>
        </v-row>
        <v-card-title class="font-weight-black">NAT契約一覧</v-card-title>
        <div class="d-flex flex-row justify-end mb-2">
          <v-btn color="primary" @click="load">一覧更新</v-btn>
        </div>
        <app-table
          v-model:table-sort-by="natSetting.sortBy"
          primary-key="vpnVnCode"
          :fields="natSetting.fields"
          :items="natSetting.items"
          :filter-option="natSetting.filter_option"
          :filter="natSetting.filter"
          :hover="true"
          @click:row="showSettingDetails"
          :tr-class="rowHighlight"
        >
          <template #cell(vnName)="data">
            {{ $filter.emptyTo(data.item.vnName, "-") }}
          </template>
        </app-table>
      </v-card-text>

      <template v-if="selectedLineInfo">
        <v-card-title class="font-weight-black">NAT設定一覧</v-card-title>
        <v-card-text class="pa-2">
          <div class="d-flex flex-row justify-end mb-2">
            <v-btn
              color="primary"
              @click="modifyNatSettings"
              v-permission.config
              >設定</v-btn
            >
          </div>
          <app-table
            primary-key="globalIpAddress"
            :fields="natSettingDetails.fields"
            :items="natSettingDetails.items"
            sticky-header="50vh"
            :tr-class="() => undefined"
          >
            <template #cell(description)="data">
              {{ $filter.emptyTo(data.item.description, "-") }}
            </template>
          </app-table>
        </v-card-text>
      </template>
    </v-card>
  </div>
</template>

<script lang="ts">
import type { NatIpEntity, NatNaptDetailEntity } from "@/apis/MSPeeringIpApi";
import type {
  AppTableHeader,
  FilterOption,
  SortItem,
} from "@/components/AppTable.vue";
import { defineComponent } from "vue";
import type { SearchOption } from "@/components/AppSearch.vue";
import MSNatSettingModify from "@/modals/msNatSetting/MSNatSettingModify.vue";

export default defineComponent({
  name: "MSNatSettingList",
  data() {
    return {
      selectedLineInfo: null as null | NatNaptDetailEntity,
      msPeeringIpKeyId: "",
      natSetting: {
        sortBy: {
          key: "vpnVnCode",
          order: "desc",
        } as SortItem,
        fields: [
          {
            key: "vpnVnCode",
            title: "VPN/VNコード",
            sortable: true,
            width: "200px",
          },
          {
            key: "vnName",
            title: "VNネットワーク名",
            sortable: true,
            width: "200px",
          },
          {
            key: "nat",
            title: "NAT用グローバルIP数(現在/上限)",
            align: "end",
            width: "200px",
            sortable: true,
            sortByFormatted: true,
            formatter: (
              value: string,
              key: null,
              item: NatNaptDetailEntity
            ) => {
              return `${item.usedNat}/${item.upperLimitNat}`;
            },
          },
        ] as AppTableHeader<NatNaptDetailEntity>[],
        items: [] as NatNaptDetailEntity[],
        search: {
          vpnVnCode: { type: "text", label: "VPN/VNコード" },
          vnName: { type: "text", label: "VNネットワーク名" },
        } as SearchOption,
        filter: {} as Record<string, unknown>,
        filter_option: {
          vpnVnCode: "like",
          vnName: "like",
        } as FilterOption,
      },
      natSettingDetails: {
        fields: [
          {
            key: "globalIpAddress",
            title: "グローバルIP",
            width: "200px",
          },
          {
            key: "privateIpAddress",
            title: "プライベートIP",
            width: "200px",
          },
          {
            key: "description",
            title: "備考",
            width: "300px",
          },
        ] as AppTableHeader<NatIpEntity>[],
        items: [] as NatIpEntity[],
      },
      isLoaded: false,
    };
  },
  async mounted() {
    await this.load();
    this.isLoaded = true;
  },
  methods: {
    async load() {
      /** クラウド向けNAT設定情報を取得API呼び出し処理 */
      const data = await this.$api.msPeeringIp.getAll();
      this.natSetting.items = data.msPeeringList;
      this.msPeeringIpKeyId = data.msPeeringIpKeyId;

      if (this.selectedLineInfo) {
        const selectedRow = this.natSetting.items.find(
          (e) => e.vpnVnCode === this.selectedLineInfo!.vpnVnCode
        );

        if (selectedRow) {
          this.selectedLineInfo = selectedRow;
          this.natSettingDetails.items = selectedRow.msNatIpList;
        } else {
          this.selectedLineInfo = null;
        }
      }
    },

    /** Nat設定一覧の表示 */
    showSettingDetails(item: NatNaptDetailEntity) {
      this.selectedLineInfo = item;
      this.natSettingDetails.items = item.msNatIpList;
    },

    /** 選択した行の色付き */
    rowHighlight(item: NatNaptDetailEntity): string[] | undefined {
      if (item.vpnVnCode == this.selectedLineInfo?.vpnVnCode) {
        return ["table-success"];
      }
      return ["clickable"];
    },
    /** 設定画面へ遷移 */
    async modifyNatSettings() {
      await this.$modal.show(MSNatSettingModify, {
        natSettingDetails: this.selectedLineInfo,
        msPeeringIpKeyId: this.msPeeringIpKeyId,
      });
      await this.load();
    },

    /** フロント側のみでのCSVダウンロード */
    downloadCsv() {
      // NAT用グローバルIP数(現在/上限)ではなく、NAPTグローバルIP数(現在)・NATグローバルIP数(上限)に分けて表示なので、新たに列データを作る
      const csvFields = [...this.natSetting.fields];
      csvFields.splice(
        2,
        1,
        { key: "usedNat", title: "NATグローバルIP数(現在)" },
        { key: "upperLimitNat", title: "NATグローバルIP数(上限)" }
      );

      this.$service.csvService.appTableCsvDownLoad(
        "saas_nat_setting",
        this.natSetting.items.sortBy(["vpnVnCode", "desc"]),
        this.$service.csvService.genFieldList(csvFields)
      );
    },
  },
});
</script>
